// Where leftover FE talks to leftover API, and production stays on /api.
//
// Production: frontend/vercel.json rewrites /api to easypeazy-api.vercel.app
// (Vercel project easypeazy-api; easy-peazy-api.vercel.app is the kept old alias).
// Leftover easypeazy-web *.vercel.app preview hosts cannot use that rewrite — it
// would hit production, which still sells transfer at-cost ($11.40 + ICANN).
// Leftover host must win over REACT_APP_BACKEND_URL: Preview builds can bake
// the production API, and leftover FE then shows the live quote, not this PR.

// A preview frontend calls the easypeazy-api preview built from the SAME git
// branch, via Vercel's branch alias `easypeazy-api-git-<branch>-<team>.vercel.app`.
// The branch comes from the build (frontend/vercel.json passes Vercel's
// VERCEL_GIT_COMMIT_REF in as REACT_APP_VERCEL_GIT_COMMIT_REF). Over 63
// characters Vercel cuts the branch short and adds a 6-character hash:
// sha256("git-" + branch + project), first 6 hex (checked against real
// aliases 2026-10-25). The browser can't hash synchronously, so the build
// computes it (scripts/api-branch-hash.mjs -> REACT_APP_API_BRANCH_HASH).
// Without it a long branch falls back to the /api rewrite, i.e. production.
const API_PROJECT = 'easypeazy-api';
const VERCEL_TEAM_SLUG = 'life-produces-life';
const MAX_LABEL = 63;

export function branchApiOrigin(ref, hash = '') {
  const slug = String(ref || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  if (!slug) return '';
  const prefix = `${API_PROJECT}-git-`;
  const suffix = `-${VERCEL_TEAM_SLUG}`;
  const label = `${prefix}${slug}${suffix}`;
  if (label.length <= MAX_LABEL) return `https://${label}.vercel.app`;
  if (!/^[0-9a-f]{6}$/.test(hash || '')) return '';
  const room = MAX_LABEL - prefix.length - suffix.length - hash.length - 1;
  return `https://${prefix}${slug.slice(0, room)}-${hash}${suffix}.vercel.app`;
}

export function leftoverApiOrigin() {
  // Spelled out: CRA inlines process.env.REACT_APP_* only by literal name.
  try {
    return branchApiOrigin(process.env.REACT_APP_VERCEL_GIT_COMMIT_REF || '', process.env.REACT_APP_API_BRANCH_HASH || '');
  } catch {
    return '';
  }
}

// Resolved at build time for the deployed bundle; tests use leftoverApiOrigin().
export const LEFTOVER_API_ORIGIN = leftoverApiOrigin();

// CoS leftover walk: receive is AWD PR38 leftover unique, not production my.
export const LEFTOVER_AWD_EDITOR_ORIGIN =
  'https://ai-website-design-q8w7upvs1-life-produces-life.vercel.app';
export const LEFTOVER_AWD_SHARE = 'DvRmb5mS9T8qeLL7jkhDRSrsznWQ0KMF';
const PRIOR_LEFTOVER_AWD_HOSTS = new Set([
  'ai-website-design-git-cursor-editor-h-5d2832-life-produces-life.vercel.app',
  'ai-website-design-hax4g1wob-life-produces-life.vercel.app',
]);

const PRODUCTION_FRONTEND_HOSTS = new Set([
  'easypeazy.site',
  'www.easypeazy.site',
  'my.easypeazy.site',
  'easypeazy.unitedundergod.org',
  'easypeazy-web.vercel.app',
  // Production branch/team aliases Vercel gives the renamed project.
  'easypeazy-web-life-produces-life.vercel.app',
  'easypeazy-web-git-main-life-produces-life.vercel.app',
  // Old project-name alias, kept attached after the rename. name-guard: allow
  'website-friends-kappa.vercel.app', // name-guard: allow
]);

// Preview hosts of the easypeazy-web Vercel project. Vercel shortens the
// project name in unique deployment URLs to `easypeazy-<hash>-…`, and branch
// aliases are `easypeazy-web-git-…`, so match `easypeazy-`. API hosts
// (`easypeazy-api…`) are never a frontend. The last prefix is the pre-rename
// project name; old preview URLs keep it. name-guard: allow
const LEFTOVER_FRONTEND_PREFIXES = ['easypeazy-', 'website-friends-']; // name-guard: allow

export function isProductionFrontendHost(hostname) {
  return PRODUCTION_FRONTEND_HOSTS.has((hostname || '').toLowerCase());
}

export function isLeftoverFrontendHost(hostname) {
  const host = (hostname || '').toLowerCase();
  if (!host || isProductionFrontendHost(host)) return false;
  if (host.endsWith('.easypeazy.site') || host.endsWith('.unitedundergod.org')) return false;
  if (host.startsWith('easypeazy-api')) return false;
  return host.endsWith('.vercel.app')
    && LEFTOVER_FRONTEND_PREFIXES.some((prefix) => host.startsWith(prefix));
}

export function resolveBackendRoot(hostname) {
  const host = (hostname
    || (typeof window !== 'undefined' ? window.location.hostname : '')
    || '').toLowerCase();
  if (isLeftoverFrontendHost(host)) {
    const origin = leftoverApiOrigin();
    if (origin) return origin;
    return '';
  }
  const raw = (typeof process !== 'undefined'
    ? (process.env.REACT_APP_BACKEND_URL || '')
    : '').trim().replace(/\/$/, '');
  // Dead hosts we must never fall back to, however they got configured.
  if (raw && !/localhost|127\.0\.0\.1|api\.easypeazy\.site|easy-peazy-api\.onrender\.com/.test(raw)) {
    return raw;
  }
  if (/localhost|127\.0\.0\.1/.test(host)) {
    return raw || 'http://localhost:8000';
  }
  return '';
}

// Resolve at call time. A module-load snapshot can be empty before
// window.location exists, which would send leftover FE to relative /api
// (production rewrite) or a stale host.
export function apiUrl(path = '', hostname) {
  const root = resolveBackendRoot(hostname);
  const suffix = !path ? '' : (path.startsWith('/') ? path : `/${path}`);
  return `${root}/api${suffix}`;
}

/**
 * Leftover FE only: hop the EXIST minted SSO URL onto the AWD PR38 leftover
 * host so Admin → Edit Website can land /edit/{id} signed in. Production
 * easypeazy.site keeps my.easypeazy.site / my.churchconnect.cloud.
 */
export function leftoverEditorHopUrl(href, hostname) {
  const pageHost = (hostname
    || (typeof window !== 'undefined' ? window.location.hostname : '')
    || '');
  if (!href || !isLeftoverFrontendHost(pageHost)) return href;
  let parsed;
  try {
    parsed = new URL(href);
  } catch {
    return href;
  }
  const dest = new URL(LEFTOVER_AWD_EDITOR_ORIGIN);
  const host = parsed.hostname.toLowerCase();
  const isEditor = host === 'my.easypeazy.site'
    || host === 'my.churchconnect.cloud'
    || host === dest.hostname
    || PRIOR_LEFTOVER_AWD_HOSTS.has(host);
  if (!isEditor) return href;
  const query = parsed.search || '';
  const extra = (LEFTOVER_AWD_SHARE && !/(?:^|[?&])_vercel_share=/.test(query))
    ? `${query ? '&' : '?'}_vercel_share=${encodeURIComponent(LEFTOVER_AWD_SHARE)}`
    : '';
  return `${dest.origin}${parsed.pathname}${query}${extra}${parsed.hash}`;
}
